/**
 * HelpMenu.jsx — Help top-level menu.
 *
 * Items: Accredited Training Library, Training Center, Loom Manual,
 * Community, Keyboard Shortcuts, Help Mode toggle, About.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenuSeparator, DropdownMenuShortcut,
  DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { GraduationCap, HelpCircle, Compass, Feather, EyeOff, Download } from 'lucide-react';
import { openTrainingCenter } from '@/components/TrainingCenter';
import { getPromptMode, setPromptMode } from '@/components/OnboardingPromptModeDialog';
import { Menu, MenuItem } from './shared';

const HELP_MODES = [
  {
    id: 'guided',
    icon: Compass,
    label: 'Guided',
    hint: 'Full tool tips on every menu item and button',
  },
  {
    id: 'light',
    icon: Feather,
    label: 'Light',
    hint: '"What to do next" hints only — no per-item descriptions',
  },
  {
    id: 'off',
    icon: EyeOff,
    label: 'Off',
    hint: 'No guidance prompts or in-menu descriptions',
  },
];

export function HelpMenu({ onHelp, onContextHelp, onCommunity, onAbout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(() => getPromptMode() || 'off');

  const changeMode = (next) => {
    setPromptMode(next);
    setMode(next);
  };

  // ── PWA install prompt ────────────────────────────────────────────
  const [canInstall, setCanInstall] = useState(() => !!window.__writer_pwa_prompt);
  const [installed, setInstalled]   = useState(false);
  const [iosInstructions, setIosInstructions] = useState(false);

  useEffect(() => {
    const onReady = () => setCanInstall(true);
    const onInstalled = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener('cr:writer-pwa-prompt-ready', onReady);
    window.addEventListener('cr:writer-pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('cr:writer-pwa-prompt-ready', onReady);
      window.removeEventListener('cr:writer-pwa-installed', onInstalled);
    };
  }, []);

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const handleInstall = async () => {
    if (window.__writer_pwa_prompt) {
      window.__writer_pwa_prompt.prompt();
      const result = await window.__writer_pwa_prompt.userChoice;
      if (result?.outcome === 'accepted') {
        window.__writer_pwa_prompt = null;
        setCanInstall(false);
        setInstalled(true);
      }
    } else if (isIos || isSafari) {
      setIosInstructions(true);
    }
  };

  return (
    <Menu label={t('menu.help')}>
      <MenuItem onClick={() => navigate('/training')} data-testid="menu-help-accredited-training">
        <GraduationCap className="h-3.5 w-3.5 text-emerald-700" /> {t('menu.accreditedTrainingLibrary')}
      </MenuItem>
      <MenuItem onClick={openTrainingCenter} data-testid="menu-help-training-center">
        <GraduationCap className="h-3.5 w-3.5 text-oxblood-600" /> {t('menu.trainingCenterFull')}
      </MenuItem>
      <DropdownMenuSeparator />
      <MenuItem onClick={onHelp} data-testid="menu-help-manual">
        {t('menu.theLoomManual')}<DropdownMenuShortcut>F1</DropdownMenuShortcut>
      </MenuItem>
      <MenuItem onClick={onContextHelp} data-testid="menu-help-context">
        <HelpCircle className="h-3.5 w-3.5 text-rose-700" /> Context Help
      </MenuItem>
      <MenuItem onClick={onCommunity} data-testid="menu-help-community">{t('menu.community')}</MenuItem>
      <MenuItem>{t('menu.keyboardShortcuts')}<DropdownMenuShortcut>Ctrl+K Ctrl+S</DropdownMenuShortcut></MenuItem>

      {/* ── Install App (PWA) ─────────────────────────────────────── */}
      {!installed && (canInstall || isIos || isSafari) && (
        <>
          <DropdownMenuSeparator />
          <MenuItem onClick={handleInstall} data-testid="menu-help-install-app">
            <Download className="h-3.5 w-3.5 text-primary" />
            Install App
            <span className="ml-auto text-[10px] text-muted-foreground">Add to home screen</span>
          </MenuItem>
        </>
      )}
      {installed && (
        <>
          <DropdownMenuSeparator />
          <MenuItem disabled data-testid="menu-help-install-app">
            <Download className="h-3.5 w-3.5 text-emerald-500" />
            App installed
          </MenuItem>
        </>
      )}

      {/* iOS step-by-step instructions sheet */}
      {iosInstructions && (
        <div className="mx-2 mb-1 mt-0 rounded-sm border border-border bg-muted/50 p-2.5 text-[11px] leading-relaxed">
          <p className="mb-1.5 font-semibold text-foreground">Add to Home Screen (iOS)</p>
          <ol className="space-y-1 text-muted-foreground list-decimal list-inside">
            <li>Tap the <strong>Share</strong> button in Safari's toolbar</li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
            <li>Tap <strong>Add</strong> in the top-right corner</li>
          </ol>
          <button
            className="mt-2 text-[10px] text-muted-foreground/70 hover:text-foreground"
            onClick={() => setIosInstructions(false)}
          >Dismiss</button>
        </div>
      )}

      {/* ── Guidance / Help mode picker ─────────────────────────────── */}
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        <Compass className="h-3 w-3" /> Help Mode
        <span className="ml-auto text-[10px] normal-case tracking-normal font-normal opacity-70">
          {HELP_MODES.find(m => m.id === mode)?.label ?? 'Off'}
        </span>
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup value={mode} onValueChange={changeMode} data-testid="help-mode-radio-group">
        {HELP_MODES.map(({ id, icon: Icon, label, hint }) => (
          <DropdownMenuRadioItem
            key={id}
            value={id}
            data-testid={`help-mode-${id}`}
            className="flex-col items-start gap-0 py-1.5 text-[12.5px] cursor-pointer"
          >
            <div className="flex w-full items-center gap-2">
              <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              {label}
            </div>
            <p className="mt-0.5 pl-[22px] text-[10.5px] leading-snug text-muted-foreground/70 font-normal">
              {hint}
            </p>
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>

      <DropdownMenuSeparator />
      <MenuItem onClick={onAbout}>{t('menu.aboutTheLoom')} V2.38_f55</MenuItem>
    </Menu>
  );
}
